import {
  createGrid,
  GridOptions,
  ColDef,
  CellClassParams,
  CellValueChangedEvent,
  ProcessDataFromClipboardParams,
  UndoEndedEvent,
  IRowNode,
  Column,
  SuppressKeyboardEventParams,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../../../application/toolkit/ag_grid_base_component/ag_grid_base_controller";
import { ebEditableTheme } from "../../../application/toolkit/ag_grid_shared/eb_theme";
import { ebFilterParams, ebLocaleText } from "../../../application/toolkit/ag_grid_shared/filter_defaults";
import { NO_CLIENT_EXPORT } from "../../../application/toolkit/ag_grid_shared/no_client_export";
import {
  autoFocusFirstCell,
  EXCEL_ENTER_NAVIGATION,
  firstEditableColumn,
  focusCell,
} from "../../../application/toolkit/ag_grid_shared/excel_navigation";
import { showConfirmModal } from "../../../application/toolkit/ag_grid_shared/confirm_modal";
import { trimBlankEdgeRows } from "./clipboard_rows";
import { ChecklistCellEditor, ChecklistCellEditorParams, ListParams } from "./checklist_cell_editor";
import { SuggestionCellEditor, SuggestionCellEditorParams } from "./suggestion_cell_editor";

// Plain flat rows keyed by column id (== schema key).
type ProvisionRow = Record<string, string>;

// Shared shape returned by every provisioning domain's validator; `rows` is 1-indexed
// into the submitted payload, `field` is the schema key (== this grid's colId).
interface FieldCentricError {
  rows: number[];
  field: string;
  message: string;
}

interface HandsontableErrorsDetail {
  errors: FieldCentricError[];
}

interface StagedFile {
  name: string;
  extension: string;
}

interface StagedFilesChangedDetail {
  names: string[];
  files: StagedFile[];
}

const ERROR_CELL_CLASS = "ag-grid-provision-cell--error";
const PASTE_NOTICE_TIMEOUT_MS = 10000;
// Beyond this the notice names a count instead of a wall of headers.
const PASTE_NOTICE_NAME_CAP = 3;
const STAGED_FILES_CHANGED_EVENT = "api-dashboard:staged-files-changed";
// Marker apis_dashboards_component.rb emits for a `values:` column; not AG-Grid's own
// agSelectCellEditor name, since the list is now suggestions, not a closed set.
const VALUES_CELL_EDITOR = "suggestionCellEditor";

// AG-Grid opens the editor on Backspace (outside macOS) but clears the range on Delete. For a
// checklist column, re-issue Backspace as Delete so AG-Grid's own range clear (multi-cell, one
// undo step) runs; there is no public API for it. Never while the editor is open.
function backspaceActsAsDelete({ event, editing }: SuppressKeyboardEventParams): boolean {
  if (event.key !== "Backspace" || editing) return false;
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return false;

  if (event.type === "keydown" && event.target) {
    event.preventDefault();
    event.target.dispatchEvent(new KeyboardEvent("keydown", { key: "Delete", bubbles: true, cancelable: true }));
  }
  return true;
}

/**
 * Growable, bulk-paste AG-Grid controller for the API Dashboard's `create` paste grid --
 * a sibling to Application::Toolkit::AgGridGrowableComponent, not a subclass of it. Reuses
 * the growable's contract-agnostic widget mechanics (paste-to-grow, auto-append blank row,
 * Clear Data, single-paste undo-un-grow, autoHeight, cellClassRules error highlighting),
 * but drops every MD-specific surface: no `validateUrl`, no debounce loop, no
 * `election_errors` key, no cross-controller document events.
 *
 * No row cap: paste/append always grow to fit, unlike the growable's maxRowsValue-bounded
 * growth (an accepted risk).
 *
 * Gather-for-submit reuses the legacy Handsontable widget's DOM-event contract unchanged:
 * listens for `gatherHandsontableData` on `document`, dispatches `handsontable:data` with
 * `{rows}` on this component's root (`.handsontable-wrapper`), which the orchestrator
 * (api_dashboard_controller.ts) already listens for -- that file stays fully unedited.
 */
export default class extends AgGridBaseController {
  static targets = [...AgGridBaseController.targets, "pasteNotice"];

  static values = {
    ...AgGridBaseController.values,
    initialBlankRows: Number,
    attachmentColumns: Object,
    valueClearColumns: Array,
  };

  declare readonly pasteNoticeTarget: HTMLElement;
  declare readonly hasPasteNoticeTarget: boolean;

  declare readonly initialBlankRowsValue: number;
  // colId => extensions that column suggests; the server derives it, never this file.
  declare readonly attachmentColumnsValue: Record<string, string[]>;
  // colIds a Clear Values Only keeps (the tab's ID columns); empty on tabs without the button.
  declare readonly valueClearColumnsValue: string[];

  // Keyed by rowIndex; full replacement on every applyErrors() call.
  private errorMap: Map<number, Set<string>> = new Map();

  // Current column set. Starts as columnDefsValue; the Language API dashboard swaps it
  // at runtime via rebuildColumns() (base schema columns + dynamic language columns).
  private activeColumnDefs: ColDef[] = [];

  // Row nodes the most recent paste added, so undo can also remove them (handleUndoEnded).
  private lastPasteGrowthNodeIds: string[] | null = null;

  // Read by SuggestionCellEditor#init on every edit start; replaced wholesale on each
  // staged-files-changed event, never mutated mid-edit.
  private stagedFiles: StagedFile[] = [];

  private pasteNoticeTimer: number | null = null;

  private gatherListener: (() => void) | null = null;
  private errorsListener: ((event: Event) => void) | null = null;
  private stagedFilesChangedListener: ((event: Event) => void) | null = null;

  connect() {
    super.connect(); // license key + outside click handler

    if (!this.hasGridTarget) return;

    this.activeColumnDefs = this.columnDefsValue;

    const options: GridOptions = {
      theme: ebEditableTheme,
      localeText: ebLocaleText,
      columnDefs: this.buildProvisionColumnDefs(this.activeColumnDefs),
      rowData: this.buildBlankRows(this.initialBlankRowsValue),
      // "Growable" means the box grows with the row count, not an internal scroll viewport.
      domLayout: "autoHeight",
      // Without this, autoSizeNonFlexColumns() can't measure header text for columns
      // not yet rendered (virtualisation), so far-right columns truncate at 200px.
      suppressColumnVirtualisation: true,
      suppressMovableColumns: true,
      suppressDragLeaveHidesColumns: true,
      suppressRowDrag: true,
      rowGroupPanelShow: "never",
      cellSelection: true,
      // 50 is AG-Grid's minimum render width for this column; lower values are ignored.
      rowNumbers: { width: 50, minWidth: 50 },
      stopEditingWhenCellsLoseFocus: true,
      ...EXCEL_ENTER_NAVIGATION,
      undoRedoCellEditing: true,
      processDataFromClipboard: (params: ProcessDataFromClipboardParams) => this.growGridForPaste(params),
      onCellValueChanged: (event: CellValueChangedEvent) => this.handleCellValueChanged(event),
      onFirstDataRendered: (event) => {
        this.autoSizeNonFlexColumns();
        autoFocusFirstCell(event.api, this.gridTarget);
      },
      onUndoEnded: (event: UndoEndedEvent) => this.handleUndoEnded(event),
      // Menu popups render outside the grid's own (short/narrow) bounding box instead
      // of being clipped by it.
      popupParent: document.body,
      getMainMenuItems: (params) => this.resetColumnsOnlyMainMenuItems(params),
      defaultColDef: {
        editable: true,
        sortable: false,
        resizable: true,
        suppressMovable: true,
        // Right-click still opens the (now Reset-Columns-only) menu; only the
        // always-visible 3-dot button is removed.
        suppressHeaderMenuButton: true,
        filterParams: ebFilterParams,
      },
      ...this.gridOptionsValue,
      ...NO_CLIENT_EXPORT,
    };

    this.gridApi = createGrid(this.gridTarget, options);

    this.gridBlurHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (this.gridTarget.contains(target)) return;
      this.gridApi?.clearCellSelection();
    };
    document.addEventListener("mousedown", this.gridBlurHandler);

    // Reuses the legacy widget's DOM-event contract: orchestrator dispatches this on
    // submit and listens for the `handsontable:data` response.
    this.gatherListener = () => this.gatherAndDispatch();
    document.addEventListener("gatherHandsontableData", this.gatherListener);

    // Fed by both the sync-422 path and the Cable commit-failure path.
    this.errorsListener = (event: Event) =>
      this.applyErrors((event as CustomEvent<HandsontableErrorsDetail>).detail?.errors ?? []);
    this.element.addEventListener("handsontable:errors", this.errorsListener);

    // Dispatched on the dashboard root, outside this component's subtree; it bubbles to document.
    this.stagedFilesChangedListener = (event: Event) => {
      this.stagedFiles = (event as CustomEvent<StagedFilesChangedDetail>).detail?.files ?? [];
    };
    document.addEventListener(STAGED_FILES_CHANGED_EVENT, this.stagedFilesChangedListener);
  }

  disconnect() {
    if (this.gatherListener) {
      document.removeEventListener("gatherHandsontableData", this.gatherListener);
      this.gatherListener = null;
    }
    if (this.errorsListener) {
      this.element.removeEventListener("handsontable:errors", this.errorsListener);
      this.errorsListener = null;
    }
    if (this.stagedFilesChangedListener) {
      document.removeEventListener(STAGED_FILES_CHANGED_EVENT, this.stagedFilesChangedListener);
      this.stagedFilesChangedListener = null;
    }
    if (this.pasteNoticeTimer !== null) {
      window.clearTimeout(this.pasteNoticeTimer);
      this.pasteNoticeTimer = null;
    }
    super.disconnect();
  }

  // ── Abstract overrides — this grid has no basic/advanced toggle ──────────

  switchToBasic(): void {
    // no-op: view_toggle is never enabled for this component
  }

  switchToAdvanced(): void {
    // no-op: view_toggle is never enabled for this component
  }

  // ── Gather-for-submit ──────────────────────────────────────────────────────

  // Public: returns every row, including blank ones (parity with the growable's
  // equivalent public method for outlet-based callers).
  gatherRows(): ProvisionRow[] {
    const rows: ProvisionRow[] = [];
    this.gridApi?.forEachNode((node) => {
      if (node.data) rows.push(node.data as ProvisionRow);
    });
    return rows;
  }

  private gatherAndDispatch(): void {
    const rows = this.gatherNonBlankRows();
    this.element.dispatchEvent(new CustomEvent("handsontable:data", { detail: { rows }, bubbles: true }));
  }

  private gatherNonBlankRows(): ProvisionRow[] {
    const rows: ProvisionRow[] = [];
    this.gridApi?.forEachNode((node) => {
      if (!node.data) return;
      const row = node.data as ProvisionRow;
      if (!this.rowHasContent(row)) return;
      rows.push(row);
    });
    return rows;
  }

  // ── Column defs / error highlighting ──────────────────────────────────────

  private buildProvisionColumnDefs(columnDefs: ColDef[]): ColDef[] {
    return columnDefs.map((colDef) => {
      const colId = this.resolveColId(colDef);
      const extensions = colId ? this.attachmentColumnsValue?.[colId] : undefined;
      return {
        ...colDef,
        ...this.suggestionEditorFor(colDef, extensions),
        cellClassRules: {
          [ERROR_CELL_CLASS]: (params: CellClassParams) => this.isCellInError(params.node.rowIndex, colId),
        },
      };
    });
  }

  // Attachment and `values:` columns render through SuggestionCellEditor (only the suggestion
  // source differs); a `list` column gets the grouped ChecklistCellEditor instead.
  private suggestionEditorFor(colDef: ColDef, extensions: string[] | undefined): Partial<ColDef> {
    if (Array.isArray(extensions)) {
      return {
        cellEditor: SuggestionCellEditor,
        cellEditorParams: {
          getSuggestions: () => this.stagedNamesFor(extensions),
        } as Partial<SuggestionCellEditorParams>,
      };
    }

    if (colDef.cellEditor !== VALUES_CELL_EDITOR) return {};
    const list = (colDef.cellEditorParams as { list?: ListParams } | undefined)?.list;
    if (list) {
      return {
        cellEditor: ChecklistCellEditor,
        cellEditorParams: { list } as Partial<ChecklistCellEditorParams>,
        suppressKeyboardEvent: backspaceActsAsDelete,
      };
    }

    const serverParams = colDef.cellEditorParams as { values?: string[]; labels?: Record<string, string> } | undefined;
    const serverValues = serverParams?.values ?? [];
    const labels = serverParams?.labels;

    return {
      cellEditor: SuggestionCellEditor,
      cellEditorParams: {
        getSuggestions: () => serverValues,
        ...(labels ? { labels } : {}),
      } as Partial<SuggestionCellEditorParams>,
    };
  }

  // Suggestions only — an omitted name is still typeable, and the server still judges it.
  private stagedNamesFor(extensions: string[]): string[] {
    const allowed = new Set(extensions);
    return this.stagedFiles.filter((file) => allowed.has(file.extension)).map((file) => file.name);
  }

  private isCellInError(rowIndex: number | null, colId: string | undefined): boolean {
    if (rowIndex === null || !colId) return false;
    const cols = this.errorMap.get(rowIndex);
    return !!cols && cols.has(colId);
  }

  private resolveColId(colDef: ColDef): string | undefined {
    return colDef.colId ?? (typeof colDef.field === "string" ? colDef.field : undefined);
  }

  private getColKeys(columnDefs: ColDef[]): string[] {
    return columnDefs
      .map((colDef) => this.resolveColId(colDef))
      .filter((colId): colId is string => Boolean(colId));
  }

  private buildBlankRows(count: number): ProvisionRow[] {
    const colKeys = this.getColKeys(this.activeColumnDefs);
    return Array.from({ length: Math.max(count, 0) }, () => {
      const row: ProvisionRow = {};
      colKeys.forEach((key) => {
        row[key] = "";
      });
      return row;
    });
  }

  // ── Growable paste (no row cap, accepted risk) ─────────────────────

  private growGridForPaste(params: ProcessDataFromClipboardParams): string[][] | null {
    const data = trimBlankEdgeRows(params.data);
    // null makes AG-Grid skip the paste entirely (no cell write, no undo entry).
    if (data.length === 0) return null;

    if (!this.gridApi) return data;
    const focusedCell = this.gridApi.getFocusedCell();
    if (!focusedCell) return data;

    this.lastPasteGrowthNodeIds = null;
    this.reportReadOnlyPasteTargets(focusedCell.column, data);

    const totalRows = this.gridApi.getDisplayedRowCount();
    const rowsNeeded = focusedCell.rowIndex + data.length - totalRows;
    if (rowsNeeded <= 0) return data;

    const result = this.gridApi.applyTransaction({ add: this.buildBlankRows(rowsNeeded) });
    this.lastPasteGrowthNodeIds = (result?.add ?? [])
      .map((node) => node.id)
      .filter((id): id is string => id !== undefined);

    return data;
  }

  // AG-Grid drops pasted values on non-editable columns and says nothing (ClipboardService
  // #updateCellValue), so name the ones this paste is about to lose.
  private reportReadOnlyPasteTargets(focusedColumn: Column, data: string[][]): void {
    const width = data.reduce((widest, row) => Math.max(widest, row.length), 0);
    if (width === 0) return;

    const colKeys = new Set(this.getColKeys(this.activeColumnDefs));
    const columns = (this.gridApi?.getAllDisplayedColumns() ?? []).filter((column) =>
      colKeys.has(column.getColId()),
    );
    const start = columns.findIndex((column) => column.getColId() === focusedColumn.getColId());
    if (start === -1) return;

    // A blank at a read-only column's offset loses nothing, so only a value the paste actually
    // carried counts as skipped.
    const skipped = columns
      .slice(start, start + width)
      .filter(
        (column, offset) =>
          column.getColDef().editable === false && data.some((row) => (row[offset] ?? "").trim() !== ""),
      )
      .map((column) => column.getColDef().headerName ?? column.getColId());

    this.showPasteNotice(skipped);
  }

  private showPasteNotice(skipped: string[]): void {
    if (!this.hasPasteNoticeTarget) return;

    if (this.pasteNoticeTimer !== null) window.clearTimeout(this.pasteNoticeTimer);
    if (skipped.length === 0) {
      this.pasteNoticeTarget.hidden = true;
      return;
    }

    const named = skipped.slice(0, PASTE_NOTICE_NAME_CAP).join(", ");
    const rest = skipped.length - PASTE_NOTICE_NAME_CAP;
    const columns = rest > 0 ? `${named} and ${rest} more` : named;
    const one = skipped.length === 1;
    const sentence = `The pasted ${one ? "value" : "values"} for ${columns} ${one ? "was" : "were"} ignored`;

    this.pasteNoticeTarget.textContent = `${sentence} — greyed columns cannot be changed.`;
    this.pasteNoticeTarget.hidden = false;
    this.pasteNoticeTimer = window.setTimeout(() => {
      this.pasteNoticeTarget.hidden = true;
      this.pasteNoticeTimer = null;
    }, PASTE_NOTICE_TIMEOUT_MS);
  }

  // AG-Grid's undo only reverts cell values, not row structure — best-effort undo
  // the most recent paste's growth too, if those rows are still blank afterward.
  private handleUndoEnded(event: UndoEndedEvent): void {
    if (!event.operationPerformed) return;

    const nodeIds = this.lastPasteGrowthNodeIds;
    this.lastPasteGrowthNodeIds = null;
    if (!nodeIds || !this.gridApi) return;

    const nodes = nodeIds
      .map((id) => this.gridApi!.getRowNode(id))
      .filter((node): node is IRowNode<ProvisionRow> => !!node);

    if (nodes.length !== nodeIds.length) return;
    if (nodes.some((node) => !node.data || this.rowHasContent(node.data))) return;

    // No getRowId is configured, so removal must match by data-object reference, not IRowNode.
    this.gridApi.applyTransaction({ remove: nodes.map((node) => node.data!) });
  }

  // ── Auto-append blank row ───────────────────────────────────────────────────

  private handleCellValueChanged(event: CellValueChangedEvent): void {
    this.appendBlankRowIfLastRowFilled(event);
    // Re-fit columns whose content grew past the initial autosize on first render.
    this.autoSizeNonFlexColumns();
    this.dispatchGridChanged();
  }

  // Tells the orchestrator the submitted rows may no longer match whatever the operator
  // last acknowledged in the answers-destroyed confirm panel (api_dashboard_controller.ts).
  private dispatchGridChanged(): void {
    this.element.dispatchEvent(new CustomEvent("handsontable:changed", { bubbles: true }));
  }

  private appendBlankRowIfLastRowFilled(event: CellValueChangedEvent): void {
    if (!this.gridApi) return;
    const totalRows = this.gridApi.getDisplayedRowCount();
    if (event.rowIndex === null || event.rowIndex !== totalRows - 1) return;
    if (!this.rowHasContent(event.data as ProvisionRow)) return;

    this.gridApi.applyTransaction({ add: this.buildBlankRows(1) });
    this.moveFocusToAppendedRow(event);
  }

  // AG Grid's Enter ran against the old last row before this one existed, so finish the move here.
  // Gated on focus, not on Enter: popup editors live under document.body, out of reach of a grid
  // keydown listener. A commit by clicking outside the grid leaves DOM focus there.
  private moveFocusToAppendedRow(event: CellValueChangedEvent): void {
    const api = this.gridApi;
    const focused = api?.getFocusedCell();
    if (!api || !focused || event.source !== "edit" || event.rowIndex === null) return;
    if (focused.rowIndex !== event.rowIndex || focused.column !== event.column) return;
    if (!this.gridTarget.contains(document.activeElement)) return;

    const nextIndex = event.rowIndex + 1;
    focusCell(api, nextIndex, firstEditableColumn(api, nextIndex) ?? event.column);
  }

  private rowHasContent(row: ProvisionRow): boolean {
    return Object.values(row).some((value) => (value ?? "").toString().trim() !== "");
  }

  // Public: lets Load ask before overwriting; blank rows don't count.
  hasContent(): boolean {
    return this.gatherNonBlankRows().length > 0;
  }

  // ── Clear Data button ────────────────────────────────────────────────────

  // Confirms first if any row has content, then resets to initialBlankRowsValue blanks.
  async clearRows(): Promise<void> {
    if (!this.gridApi) return;

    const rows = this.gatherNonBlankRows();
    if (rows.length > 0) {
      const confirmed = await showConfirmModal({ message: "Are you sure you want to clear all rows?" });
      if (!confirmed) return;
    }

    this.replaceRowsAfterClear(this.buildBlankRows(this.initialBlankRowsValue));
  }

  // Shared by both clear buttons: every error and result the old values earned is now stale.
  private replaceRowsAfterClear(rows: ProvisionRow[]): void {
    if (!this.gridApi) return;

    this.errorMap = new Map();
    this.lastPasteGrowthNodeIds = null;
    this.showPasteNotice([]);
    this.gridApi.setGridOption("rowData", rows);
    this.gridApi.refreshCells({ force: true });
    // Columns autosized to the old content would otherwise stay wide over blank rows.
    this.autoSizeNonFlexColumns();
    this.dispatchGridChanged();
    // Not `handsontable:changed`: a single cell edit fires that too, and must keep the error list.
    this.element.dispatchEvent(new CustomEvent("handsontable:cleared", { bubbles: true }));
  }

  // ── Clear Values Only button ─────────────────────────────────────────────

  // Blanks every column the tab did not name as an ID, keeping the row count and the IDs the
  // operator looked up -- so re-entering values needs no second download.
  async clearValues(): Promise<void> {
    if (!this.gridApi) return;

    const preserved = new Set(this.valueClearColumnsValue);
    const clearedKeys = this.getColKeys(this.activeColumnDefs).filter((key) => !preserved.has(key));
    if (clearedKeys.length === 0) return;

    const rows: ProvisionRow[] = [];
    this.gridApi.forEachNode((node) => {
      if (node.data) rows.push(node.data as ProvisionRow);
    });

    const hasValues = rows.some((row) => clearedKeys.some((key) => (row[key] ?? "").toString().trim() !== ""));
    if (hasValues) {
      const confirmed = await showConfirmModal({ message: "Are you sure you want to clear all values? The ID columns are kept." });
      if (!confirmed) return;
    }

    const cleared = rows.map((row) => {
      const next: ProvisionRow = { ...row };
      clearedKeys.forEach((key) => { next[key] = ""; });
      return next;
    });

    this.replaceRowsAfterClear(cleared);
  }

  // ── Language API support (called cross-controller by api_dashboard_controller.ts) ──

  // Public: readiness probe for the orchestrator's retry loop.
  isReady(): boolean {
    return !!this.gridApi;
  }

  // Public: swaps the column set (base schema + dynamic language columns), remapping
  // existing row values onto matching keys and padding back to initialBlankRowsValue.
  rebuildColumns(columns: Array<{ key: string; header: string }>): void {
    if (!this.gridApi) return;

    this.activeColumnDefs = columns.map((column) => ({
      colId: column.key,
      field: column.key,
      headerName: column.header,
    }));
    const colKeys = this.getColKeys(this.activeColumnDefs);

    const remapped = this.gatherRows().map((row) => {
      const newRow: ProvisionRow = {};
      colKeys.forEach((key) => {
        newRow[key] = row[key] ?? "";
      });
      return newRow;
    });
    while (remapped.length < Math.max(this.initialBlankRowsValue, 0)) {
      remapped.push(...this.buildBlankRows(1));
    }

    this.errorMap = new Map();
    this.lastPasteGrowthNodeIds = null;
    this.gridApi.setGridOption("columnDefs", this.buildProvisionColumnDefs(this.activeColumnDefs));
    this.gridApi.setGridOption("rowData", remapped);
    this.autoSizeNonFlexColumns();
  }

  // Public: replaces grid contents with fetched translation rows (read_create preload),
  // keyed by the active columns and padded with blanks to initialBlankRowsValue.
  loadRows(rows: Array<Record<string, string>>): void {
    if (!this.gridApi) return;

    const colKeys = this.getColKeys(this.activeColumnDefs);
    const data = (rows ?? []).map((row) => {
      const newRow: ProvisionRow = {};
      colKeys.forEach((key) => {
        newRow[key] = (row[key] ?? "").toString();
      });
      return newRow;
    });
    while (data.length < Math.max(this.initialBlankRowsValue, 0)) {
      data.push(...this.buildBlankRows(1));
    }

    this.errorMap = new Map();
    this.gridApi.setGridOption("rowData", data);
    this.autoSizeNonFlexColumns();
  }

  // Public: re-measure after reveal — the language grids initialize inside a hidden
  // section, so column autosizing on first render measured nothing.
  refresh(): void {
    this.autoSizeNonFlexColumns();
  }

  // Maps field-centric errors onto cell highlights; `rows: []` entries have no row to
  // highlight and are surfaced via the banner in api_dashboard_controller.ts instead.
  applyErrors(errors: FieldCentricError[]): void {
    const colKeys = this.getColKeys(this.activeColumnDefs);
    this.errorMap = new Map();

    (errors ?? []).forEach((error) => {
      if (!error || typeof error.field !== "string" || !Array.isArray(error.rows)) return;

      if (!colKeys.includes(error.field)) {
        // eslint-disable-next-line no-console
        console.warn(`AgGridProvisionComponent: unresolvable field "${error.field}" in error contract`);
        return;
      }

      error.rows.forEach((rowNum) => {
        if (typeof rowNum !== "number") return;
        const rowIndex = rowNum - 1;
        if (rowIndex < 0) return;

        const existing = this.errorMap.get(rowIndex) ?? new Set<string>();
        existing.add(error.field);
        this.errorMap.set(rowIndex, existing);
      });
    });

    this.gridApi?.refreshCells({ force: true });
  }
}
